<template>
  <div class="new-project-resources" :class="{ 'automated-scoping': isAutomatedScopingEnabled }">
    <span class="resources-section">
      <div class="add-resource-section" v-if='showUpperPart'>
        <div class="add-resource-title">
          <label class="title-label sub-domain-title">
            {{ $t('newProject.resources.addResource') }}
          </label>
        </div>
        <div class="add-resource-content">
          <div class="resource-input-section add-resource">
            <advanced-resource-select
              class-name='resources-select'
              :items="listOfDomains"
              v-model="domainSelected"
              @input="onNewResourceChange"
            />
          </div>
        </div>
        <v-btn
          v-if="isAutomatedScopingEnabled"
          @click="initiateScoping"
          :disabled="allResourcesAutomaticallyScoped || automatedScoping"
          :loading="automatedScoping"
          color="primary"
          class="run-automated-scoping"
        >
          {{ $t('newProject.resources.automatedScoping.run') }}
        </v-btn>
      </div>

      <new-project-resources-list
        :configuration="configuration"
        :resources="resources"
        :automated-scoping="isAutomatedScopingEnabled"
        :automated-scoping-running="automatedScoping"
        @runAutomatedScoping="initiateScoping"
        class="new-project-resources-list"
        :class='{ scoped : !showUpperPart }'
        ref="resourceListRef"
      >
      </new-project-resources-list>
    </span>
    <error-message :errorText="errorText" @close="errorText = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';

import { FEATURES_LIST, testModeTypes } from '@/constants/projects';
import { domainsMatched } from '@/utils/stringUtils';
import { runAutomatedScoping } from '@/services/projectCalculator';

import NewProjectResourcesList from '@/components/newProject/NewProjectResourcesList.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import AdvancedResourceSelect from '@/components/common/AdvancedResourceSelect.vue';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import { LOAD_DOMAINS_FROM_CONFIGURATION } from '@/store/project/actionTypes';

export default {
  name: 'NewProjectResources',
  components: {
    NewProjectResourcesList,
    ErrorMessage,
    AdvancedResourceSelect,
  },
  props: {
    newProject: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      domainSelected: [],
      resources: this.newProject.projectResourcesData.resources,
      configuration: this.newProject.projectEnvironmentData.configuration,
      automatedScoping: false,
      errorText: '',
      scopingResults: {},
    };
  },
  async created() {
    if (this.profile) {
      await this.loadDomains();
    }
  },
  computed: {
    ...mapGetters([
      'userId',
      'configurationDomains',
      'profile',
      'domains',
      'storedNewProjectNavigation',
    ]),
    allResourcesAutomaticallyScoped() {
      return (this.resources || []).every(res => !!res.automaticallyScoped);
    },
    isAutomatedScopingEnabled() {
      return this.newProject.projectEnvironmentData.mode === testModeTypes.AUTOMATED;
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    showUpperPart() {
      return !this.resources.length || !this.resources.every(resource => resource.automaticallyScoped);
    },
  },
  watch: {
    newProject: {
      deep: true,
      handler() {
        this.$emit('setRequiredDataForStep', 3);
      },
    },
    resources: {
      deep: true,
      handler() {
        this.$emit('setRequiredDataForStep', 3, true);
        this.syncResources();
        this.syncResourcesSelected();
      },
    },
    async profile() {
      if (this.profile) {
        await this.loadDomains();
      }
    },
  },
  beforeDestroy() {
    this.removeEmptyCredentials();
    this.syncResources();
  },
  methods: {
    async loadDomains() {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
    },
    prepareFeatures() {
      return FEATURES_LIST.map(f => ({ value: f, selected: !this.isAutomatedScopingEnabled }));
    },
    onNewResourceChange(resources) {
      this.resources = [];
      for (let i = 0; i < resources.length; i += 1) {
        const resource = resources[i];
        if (
          !this.resources.find(res => (res.value && res.value.trim()) === (resource && resource.trim()))
        ) {
          if (resource && resource.trim().length) {
            const emptyCred = {
              username: null,
              password: null,
            };
            this.resources.unshift({
              key: `${moment().valueOf()}${i}`,
              value: resource,
              credentials: [emptyCred],
              features: this.$pt('newProject.resources.feature', this.prepareFeatures()),
              automaticallyScoped: false,
            });
          }
        }
      }
    },
    syncResources() {
      this.newProject.projectResourcesData.resources = [...this.resources];
    },
    syncResourcesSelected() {
      this.domainSelected = this.resources.map(resource => resource.value);
    },
    removeEmptyCredentials() {
      this.resources = this.resources.map(res => {
        const creds = res.credentials.filter(cred => cred.username?.trim().length && cred.password?.trim().length);
        if (!creds.length) {
          const emptyCred = {
            username: null,
            password: null,
          };
          creds.push(emptyCred);
        }
        return {
          ..._.cloneDeep(res),
          credentials: creds,
        };
      });
    },
    async initiateScoping() {
      this.automatedScoping = true;
      try {
        await runAutomatedScoping(this.resources, this.newProject, this.storedNewProjectNavigation);
        this.$emit('onChangeStep', 7);
      } catch (e) {
        this.automatedScoping = false;
        this.errorText = e.message;
      }
    },
    async continueAutomatedScoping() {
      if (typeof this.scopingResults !== 'object') {
        this.errorText = this.$pt('newProject.resources.automatedScoping.error', this.scopingResults);
      } else {
        this.resources.forEach((resource, i) => {
          const objectKeys = Object.keys(this.scopingResults);
          const resourceDomain = objectKeys.find(domain => domainsMatched(domain, resource.value));
          const [resourceAutomatedScope] = this.scopingResults[resourceDomain];

          if (resourceAutomatedScope) {
            this.resources[i].automaticallyScoped = true;
            this.resources[i].features = this.resources[i].features.map(feature => ({
              ...feature,
              selected: resourceAutomatedScope[feature.value] || false,
            }));
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.resources-section {
  display: block;
  margin-top: 0;
  height: 100%;
  .add-resource-section {
    position: fixed;
    background-color: var(--bg-primary);
    z-index: 100;
    padding-top: rem(25);
    width: rem(896);
    padding-left: rem(183);
    margin-top: rem(-1);
    border-top: 1px solid var(--border-secondary);
  }

  .add-resource-title {
    display: inline-block;
  }

  .delete-box-marker {
    height: rem(20);
    width: rem(20);
    border: solid 2px var(--status-error);
    float: left;
    margin-right: rem(5);
    margin-left: rem(13);
    cursor: pointer;
  }

  .minus-icon {
    position: relative;
    bottom: $space-2;
    right: rem(2);
    font-weight: 700;
    color: var(--status-error);
    font-size: rem(20);
  }

  .title-label {
    float: left;
    height: rem(10);
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-left: 0;
    margin-bottom: 0;
    text-align: left;
  }
  .resource-input-section {
    .text-field {
      box-sizing: border-box;
      height: rem(32);
      width: rem(464);
      border: 1px solid var(--border-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
    }
    input {
      box-sizing: border-box;
      height: rem(32);
      width: rem(400);
      border: 1px solid var(--border-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      padding-left: $space-3;
    }
    input:focus,
    textarea:focus,
    button:focus {
      outline: none;
    }
    margin-bottom: $space-4;
  }

  .add-resource {
    display: inline-flex;
    input {
      width: rem(464);
    }

    .add-resource-button {
      width: rem(60);
      margin-left: $space-1;
      background-color: var(--brand-primary);
      color: #ffffff;
      text-transform: uppercase;
      padding-top: rem(8.5);
      margin-bottom: $space-2;

      &.inactive {
        height: rem(32);
        width: rem(60);
        border-radius: $radius-sm;
        background-color: #bbbbbb;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 3px 0 rgba(0, 0, 0, 0.2);
        cursor: default;
      }
    }
  }

  .select-button {
    float: right;
    font-size: $font-size-sm;
    color: var(--brand-primary);
    cursor: pointer;
  }

  .delete-button {
    float: right;
    font-size: $font-size-sm;
    color: var(--status-error);
    cursor: pointer;
    margin-left: rem(22);
  }

  .solid-button {
    display: inline-block;
    padding-top: $space-2;
    height: rem(32);
    width: rem(85);
    border-radius: $radius-sm;
    background-color: var(--bg-primary);

    font-size: $font-size-sm;
    font-weight: 500;
    letter-spacing: 1.07px;
    line-height: rem(16);
    text-align: center;
    margin-top: rem(18);

    cursor: pointer;
  }
  .error-message {
    color: var(--status-error);
    font-size: $font-size-sm;
    line-height: $font-size-base;
    min-height: $font-size-base;
    margin-bottom: $space-2;
    margin-top: rem(20);
    text-align: center;
  }
}

.new-project-resources-list {
  width: rem(532);
  display: block;
  margin-left: rem(182);
  padding-top: rem(80);
  &.scoped {
    padding-top: rem(30);
  }
}

.automated-scoping .new-project-resources-list {
  padding-top: rem(150);
  &.scoped {
    padding-top: rem(30);
  }
}

#app .run-automated-scoping {
  margin-left: rem(150);
  margin-bottom: rem(20);

  &.v-btn--disabled {
    background-color: #bbb !important;
    color: #fff !important;
  }
}
</style>
